<script setup lang="ts">
useSeoMeta({
  title: 'Uses — Shakir Abdulrahim',
  ogTitle: 'Uses — Shakir Abdulrahim',
  description: 'Tools, software, hardware, and desk setup that Shakir uses daily.',
})

const { data: items } = await useFetch('/api/uses', {
  default: () => [],
})

const categoryLabels: Record<string, string> = {
  editor: 'Editors',
  terminal: 'Terminal',
  hardware: 'Hardware',
  software: 'Software',
  desk: 'Desk Setup',
}

const grouped = computed(() => {
  const groups: Record<string, any[]> = {}
  for (const item of items.value as any[]) {
    const cat = item.category || 'other'
    if (!groups[cat]) groups[cat] = []
    groups[cat].push(item)
  }
  return groups
})
</script>

<template>
  <div class="py-24">
    <div class="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
      <p class="mb-4 text-sm font-medium tracking-widest text-[var(--color-amber)] uppercase">
        Uses
      </p>
      <h1 class="mb-6 text-4xl font-bold text-[var(--color-text)] sm:text-5xl">
        My Setup
      </h1>
      <p class="mb-12 text-lg text-[var(--color-text-muted)]">
        Tools, software, and hardware I use daily for development and tinkering.
      </p>

      <div class="space-y-12">
        <section v-for="(categoryItems, category) in grouped" :key="category">
          <h2 class="mb-6 text-xl font-semibold text-[var(--color-amber)]">
            {{ categoryLabels[category as string] || category }}
          </h2>
          <div class="space-y-4">
            <div
              v-for="item in categoryItems"
              :key="item.id"
              class="rounded-xl border border-[var(--color-card-border)] bg-[var(--color-card-bg)] p-5 transition-all duration-200 hover:border-[var(--color-amber)]/20"
            >
              <div class="flex items-start justify-between gap-4">
                <div>
                  <h3 class="font-semibold text-[var(--color-text)]">
                    {{ item.name }}
                  </h3>
                  <p class="mt-1 text-sm text-[var(--color-text-muted)]">
                    {{ item.description }}
                  </p>
                </div>
                <a
                  v-if="item.url"
                  :href="item.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  :aria-label="`Visit ${item.name}`"
                  class="shrink-0 text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-amber)]"
                >
                  <UIcon name="i-lucide-external-link" class="size-4" />
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>

      <p v-if="!items?.length" class="mt-10 text-center text-[var(--color-text-muted)]">
        Coming soon.
      </p>
    </div>
  </div>
</template>
